import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { Copy, Flex, WebviewWrapper, Button } from '@elparking/components'
import Phone from '../Icons/Phone'
import Location from '../Icons/Location'
import LinkButton from '../LinkButton'
import { sendPhoneWebviewEvent } from 'commons/js/services/sendMessage'
import LocationMobile from '../Icons/LocationMobile'

const RentVehicleExtraInfoBlock = ({
    title,
    isDestinationBlock,
    phoneNumber,
    name,
    address,
    lat,
    lon,
    showContactButtons,
    isMixedBlock,
    description,
    urlCheckIn,
}) => (<Flex flexDirection='column' fontWeight='regular' padding='1em 0em'>
    <Copy fontWeight='bold' fontSize={['large', 'xxLarge']} padding='0' mb='0.5em'>
        {title}
    </Copy>
    <Copy fontSize='small' fontWeight='regular' mb='0.5em'>
        {description}
    </Copy>
    {urlCheckIn &&
    <Copy fontSize='small' fontWeight='regular' mb='1em'>
        <FormattedMessage
          defaultMessage='Recoge tu coche más rápido, completando la información con el check in online. {checkInLink}.' values={{
            checkInLink: <Button.unstyled fontSize='small' textTransform='none' as='a' href={urlCheckIn} target='_blank'><FormattedMessage defaultMessage='Ir al check in online' /></Button.unstyled>,
          }}/>
    </Copy>}
    {!isDestinationBlock &&
    <Copy fontSize='small' fontWeight='regular' mb='1em'>
        <FormattedMessage
          defaultMessage='El importe del alquiler se abonará en la recogida del vehículo.'/>
    </Copy>}
    {phoneNumber && <Flex mb='1em'>
        <Phone color='accent'/>
        <Copy fontSize='small' fontWeight='regular' padding='0' ml='0.5em' width='fit-content'>{phoneNumber}</Copy>
        </Flex>}
    {(name && address) && <Flex mb='1em'>
        <WebviewWrapper fallbackComponent={<Location color='accent'/>}><LocationMobile color='accent'/></WebviewWrapper>
        <Flex flexDirection='column'>
            <Copy fontSize='small' fontWeight='regular' padding='0' ml='0.5em' width='fit-content'>{name}
                <Copy as='span' fontSize='small' fontWeight='regular' ml='0.5em'>
                    {address}
                </Copy>
            </Copy>
            <Copy fontSize='small' fontWeight='regular' padding='0' ml='0.5em' width='fit-content'>
                {(lat && lon)
                    ? <Copy fontSize='inherit' padding='0' fontWeight='regular'><FormattedMessage defaultMessage='Coordenadas GPS: {coordinates}' values={{
                        coordinates: <Copy textDecoration='underline' fontSize='inherit' fontWeight='regular' padding='0' as='a' target='_blank' rel='noopener noreferrer' href={`https://maps.google.com/?q=${lat},${lon}`}>{`https://maps.google.com/?q=${lat},${lon}`}</Copy>,
                }}/></Copy>
                    : address}
            </Copy>
        </Flex>
        </Flex>}
    <Copy fontSize='xSmall' fontWeight='regular'>
        <FormattedMessage
          defaultMessage='Si necesitas gestionar la recogida o la devolución de tu vehículo fuera del horario de atención de la oficina, consultarnos previamente para indicarte como proceder en tu caso (se aplicarán cargos).'/>
    </Copy>
    {showContactButtons && (isDestinationBlock || isMixedBlock) && <LinkButton onClick={() => sendPhoneWebviewEvent(phoneNumber)} >
        <FormattedMessage defaultMessage='Contactar con la oficina'/>
        </LinkButton>}
</Flex>)

RentVehicleExtraInfoBlock.propTypes = {
    title: PropTypes.element,
    description: PropTypes.element,
    isDestinationBlock: PropTypes.bool,
    name: PropTypes.string,
    address: PropTypes.string,
    lat: PropTypes.number,
    lon: PropTypes.number,
    phoneNumber: PropTypes.string,
    showContactButtons: PropTypes.bool,
    isMixedBlock: PropTypes.bool,
    urlCheckIn: PropTypes.string,
}

const RentVehicleExtraInfo = ({
    originPOI,
    destinationPOI,
    showContactButtons,
    urlCheckIn,
}) => {
    const isOriginAndDestinationTheSame = originPOI.lat === destinationPOI.lat && originPOI.lon === destinationPOI.lon

    if (isOriginAndDestinationTheSame) {
        return <RentVehicleExtraInfoBlock
          title={<FormattedMessage defaultMessage='¿Cómo recojo y devuelvo mi vehículo?'/>}
          isDestinationBlock={false}
          description={<FormattedMessage defaultMessage='Acércate al mostrador de la compañía que hayas elegido. Nuestro personal te facilitará toda la información que necesites para la recogida y devolución de tu vehículo.'/>}
          isMixedBlock
          phoneNumber={originPOI.phoneNumber}
          name={originPOI.stationName}
          address={originPOI.address}
          lat={originPOI.lat}
          lon={originPOI.lon}
          showContactButtons={showContactButtons}
          urlCheckIn={urlCheckIn}
        />
    }
    return <Flex flexDirection='column'>
        <RentVehicleExtraInfoBlock
          title={<FormattedMessage defaultMessage='¿Cómo recojo mi vehículo?'/>}
          description={<FormattedMessage defaultMessage='Acércate al mostrador de la compañía que hayas elegido. Nuestro personal te facilitará toda la información que necesites para la recogida de tu vehículo.'/>}
          isDestinationBlock={false}
          isMixedBlock={false}
          phoneNumber={originPOI.phoneNumber}
          name={originPOI.stationName}
          address={originPOI.address}
          lat={originPOI.lat}
          lon={originPOI.lon}
          showContactButtons={showContactButtons}
          urlCheckIn={urlCheckIn}
    />
        <RentVehicleExtraInfoBlock
          title={<FormattedMessage defaultMessage='¿Y cómo lo devuelvo?'/>}
          description={<FormattedMessage defaultMessage='Acércate al mostrador de la compañía que hayas elegido. Nuestro personal te facilitará toda la información que necesites para la devolucion de tu vehículo.'/>}
          isDestinationBlock
          isMixedBlock={false}
          phoneNumber={destinationPOI.phoneNumber}
          name={destinationPOI.stationName}
          address={destinationPOI.address}
          lat={destinationPOI.lat}
          lon={destinationPOI.lon}
          showContactButtons={showContactButtons}
    />
    </Flex>
}

RentVehicleExtraInfo.propTypes = {
    originPOI: PropTypes.shape({
        id: PropTypes.number,
        stationName: PropTypes.string,
        address: PropTypes.string,
        lat: PropTypes.number,
        lon: PropTypes.number,
        phoneNumber: PropTypes.string,
    }),
    destinationPOI: PropTypes.shape({
        id: PropTypes.number,
        stationName: PropTypes.string,
        address: PropTypes.string,
        lat: PropTypes.number,
        lon: PropTypes.number,
        phoneNumber: PropTypes.string,
    }),
    showContactButtons: PropTypes.bool,
    urlCheckIn: PropTypes.string,
}

RentVehicleExtraInfo.defaultProps = {
    showContactButtons: false,
    urlCheckIn: null,
}

export default RentVehicleExtraInfo
